import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useToast } from './ui/Toast';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { KvTable, KvRow } from './ui/KvTable';
import { ensureTrailingEmptyKvRow, makeKvRowId } from '../utils/kvRows';
import StreamRequestBar from './ui/StreamRequestBar';
import StreamLogTable from './ui/StreamLogTable';
import { SSEParser } from '../utils/sseParser';
import { MAX_LOG_MESSAGES } from '../utils/liveConnections';
import './streamBuilders.css';
import { createScopedVariableResolver, storedScopesFrom } from '../utils/scopedVariableResolver';
import { recordFeature, REQUEST } from '../services/telemetry';
import { openSseStream } from '../utils/sseTransport';
import { NO_FOLDER_VARS } from '../utils/folderVariables';

function formatElapsed(ms) {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

let _evCounter = 0;
function nextEvId() { return `sse-ev-${Date.now()}-${++_evCounter}`; }

const SSEBuilder = ({
  request,
  onDataChange,
  selectedEnvironment,
  workspaceVariables = [],
  globalVariables = [],
  collectionVariables = [],
  folderVariables = NO_FOLDER_VARS,
}) => {
  const { showToast } = useToast();

  const [url, setUrl] = useState(request?.url ?? '');
  const [method, setMethod] = useState(request?.method === 'POST' ? 'POST' : 'GET');
  // Always ends with ONE empty row: type in it and the next one appears — the same table
  // as the HTTP builder's Headers (no "Add header" button). Blank rows are never sent.
  const [headers, setHeaders] = useState(() => ensureTrailingEmptyKvRow(request?.headers, 'sseh'));
  const [body, setBody] = useState(request?.body ?? '');
  const [auth, setAuth] = useState(request?.auth ?? { type: 'none' });
  const [activeTab, setActiveTab] = useState('headers');
  const [status, setStatus] = useState('idle');
  const [{ events, dropped }, setLog] = useState({ events: [], dropped: 0 });
  // bounded like the WebSocket log: a dead URL retries forever, one error row each time
  const appendRow = useCallback((row) => {
    setLog((prev) => {
      const all = [...prev.events, row];
      const overflow = Math.max(0, all.length - MAX_LOG_MESSAGES);
      return { events: overflow ? all.slice(overflow) : all, dropped: prev.dropped + overflow };
    });
  }, []);
  /** A row about the CONNECTION — in the same log as the events (see ui/StreamLogTable). */
  const logConnection = useCallback((event, text, detail = null) => {
    appendRow({ id: nextEvId(), kind: 'event', event, data: text, detail, timestamp: Date.now() });
  }, [appendRow]);
  const [elapsed, setElapsed] = useState(0);

  const abortStreamRef = useRef(null); // the open stream's abort (utils/sseTransport)
  const retryTimeoutRef = useRef(null);
  const retryIntervalRef = useRef(3000);
  const parserRef = useRef(new SSEParser());
  const elapsedIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const prevRequestIdRef = useRef(request?.id);
  useEffect(() => {
    if (request?.id && request.id !== prevRequestIdRef.current) {
      prevRequestIdRef.current = request.id;
      setUrl(request.url ?? '');
      setMethod(request.method === 'POST' ? 'POST' : 'GET');
      setHeaders(ensureTrailingEmptyKvRow(request.headers, 'sseh'));
      setBody(request.body ?? '');
      setAuth(request.auth ?? { type: 'none' });
    }
  }, [request?.id, request?.url, request?.method, request?.headers, request?.body, request?.auth]);

  useEffect(() => {
    return () => {
      clearTimeout(retryTimeoutRef.current);
      clearInterval(elapsedIntervalRef.current);
      abortStreamRef.current?.();
    };
  }, []);

  // THE resolver every request kind sends with (utils/scopedVariableResolver).
  const substituteVariables = useMemo(
    () => createScopedVariableResolver(storedScopesFrom({
      folderVariables, collectionVariables, selectedEnvironment, workspaceVariables, globalVariables,
    })),
    [folderVariables, collectionVariables, selectedEnvironment, workspaceVariables, globalVariables]
  );

  const buildFetchHeaders = useCallback((hdrs, authConfig, lastEventId) => {
    const result = {};
    for (const h of hdrs) {
      if (h.enabled && h.key?.trim()) {
        result[substituteVariables(h.key.trim())] = substituteVariables(h.value ?? '');
      }
    }
    result['Accept'] = 'text/event-stream';
    result['Cache-Control'] = 'no-cache';
    if (authConfig.type === 'bearer' && authConfig.token?.trim()) {
      result['Authorization'] = `Bearer ${substituteVariables(authConfig.token.trim())}`;
    }
    if (lastEventId) result['Last-Event-ID'] = lastEventId;
    return result;
  }, [substituteVariables]);

  const startElapsedTimer = useCallback((t0) => {
    startTimeRef.current = t0;
    clearInterval(elapsedIntervalRef.current);
    elapsedIntervalRef.current = setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 100);
  }, []);

  const stopElapsedTimer = useCallback(() => {
    clearInterval(elapsedIntervalRef.current);
  }, []);

  const doConnect = useCallback((lastEventId, currentMethod, currentUrl, currentHeaders, currentBody, currentAuth) => {
    const resolvedUrl = substituteVariables(currentUrl.trim());
    if (!resolvedUrl) {
      showToast('Enter a URL to connect', 'warning');
      return;
    }

    abortStreamRef.current?.();
    parserRef.current.reset();

    const t0 = Date.now();
    setStatus('connecting');
    setElapsed(0);
    startElapsedTimer(t0);

    const reqHeaders = buildFetchHeaders(currentHeaders, currentAuth, lastEventId);
    const request = { method: currentMethod, url: resolvedUrl, headers: reqHeaders };
    if (currentMethod === 'POST' && currentBody.trim()) {
      request.body = substituteVariables(currentBody);
      if (!reqHeaders['Content-Type']) reqHeaders['Content-Type'] = 'application/json';
    }

    // Desktop: the same Tauri adapter as an HTTP request (the webview's fetch is CSP-blocked there).
    abortStreamRef.current = openSseStream(request, {
      onOpen: ({ status, statusText, contentType }) => {
        setStatus('connected');
        logConnection('connected', `Connected to ${resolvedUrl}`, { url: resolvedUrl, message: `${status} ${statusText || 'OK'} · ${contentType || 'no content-type'}` });
        showToast('SSE connected', 'success');
      },
      onText: (text) => {
        for (const ev of parserRef.current.feed(text)) {
          if (ev.retry) retryIntervalRef.current = ev.retry;
          const isDone = ev.data === '[DONE]';
          // in the table's shape from the start: a row that never changes is never rendered again
          appendRow({
            id: nextEvId(), kind: 'message', direction: 'in', timestamp: Date.now(),
            tag: ev.event && ev.event !== 'message' ? ev.event : 'message',
            data: isDone ? '[DONE] — the stream ended' : ev.data,
          });
        }
      },
      onEnd: () => {
        stopElapsedTimer();
        logConnection('disconnected', 'Connection closed', { url: resolvedUrl, message: 'The server ended the stream.' });
        setStatus('closed');
      },
      onError: (err) => {
        stopElapsedTimer();
        const msg = err?.message || String(err);
        logConnection('error', `Could not read ${resolvedUrl}`, { url: resolvedUrl, message: `${msg} — retrying in ${Math.round(retryIntervalRef.current / 100) / 10} s.` });
        setStatus('error');
        showToast(`SSE error: ${msg}`, 'error');

        const lastId = parserRef.current.getLastEventId();
        const delay = retryIntervalRef.current;
        retryTimeoutRef.current = setTimeout(() => {
          doConnect(lastId, currentMethod, currentUrl, currentHeaders, currentBody, currentAuth);
        }, delay);
      },
    });
  }, [substituteVariables, buildFetchHeaders, startElapsedTimer, stopElapsedTimer, showToast, logConnection, appendRow]);

  const handleConnect = useCallback(() => {
    recordFeature(REQUEST.SSE);   // identifier-free: this kind was used today
    clearTimeout(retryTimeoutRef.current);
    onDataChange?.({ url, method, headers, body, auth });
    doConnect('', method, url, headers, body, auth);
  }, [url, method, headers, body, auth, onDataChange, doConnect]);

  const handleDisconnect = useCallback(() => {
    clearTimeout(retryTimeoutRef.current);
    stopElapsedTimer();
    abortStreamRef.current?.();
    logConnection('disconnected', url ? `Disconnected from ${url}` : 'Disconnected', { url, message: 'Closed from Apiboo.' });
    setStatus('closed');
    showToast('SSE disconnected', 'info');
  }, [showToast, stopElapsedTimer, logConnection, url]);

  const handleUrlKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (status === 'connected' || status === 'connecting') handleDisconnect();
    else handleConnect();
  }, [status, handleConnect, handleDisconnect]);

  const isActive = status === 'connected' || status === 'connecting';

  const statusLabelText = {
    idle: 'Idle', connecting: 'Connecting…', connected: 'Connected', error: 'Error', closed: 'Closed',
  }[status] ?? 'Idle';

  const methodOptions = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
  ];

  const updateHeader = (idx, field, value) => {
    setHeaders((prev) => {
      const next = prev.map((h, i) => (i === idx ? { ...h, [field]: value } : h));
      // the last row got a key → open the next empty one
      if (next[next.length - 1].key !== '') next.push({ key: '', value: '', enabled: true, _id: makeKvRowId('sseh') });
      onDataChange?.({ headers: next });
      return next;
    });
  };

  const removeHeader = (idx) => {
    setHeaders((prev) => {
      const next = ensureTrailingEmptyKvRow(prev.filter((_, i) => i !== idx), 'sseh');
      onDataChange?.({ headers: next });
      return next;
    });
  };

  const visibleTabs = ['headers', ...(method === 'POST' ? ['body'] : []), 'auth'];

  return (
    <div className="ws-builder h-full flex flex-col bg-white">
      {/* Request bar — the HTTP builder's: method + URL in one field group, Connect where Send is */}
      <StreamRequestBar
        leading={(
          <CompactSelectPicker
            value={method}
            onChange={(v) => { setMethod(v); onDataChange?.({ method: v }); }}
            options={methodOptions}
            disabled={isActive}
            aria-label="HTTP method"
          />
        )}
        inputProps={{
          className: 'ws-builder__url-input',
          placeholder: 'https://api.example.com/events',
          value: url,
          // saved as typed, like the body: a tab switch re-reads the request
          onChange: (e) => { setUrl(e.target.value); onDataChange?.({ url: e.target.value }); },
          onKeyDown: handleUrlKeyDown,
          disabled: isActive,
          'aria-label': 'SSE URL',
        }}
        state={{ status, label: status === 'connected' ? `${statusLabelText} · ${formatElapsed(elapsed)}` : statusLabelText }}
        action={isActive
          ? { label: 'Disconnect', onClick: handleDisconnect, danger: true, className: 'ws-builder__btn--disconnect' }
          : { label: 'Connect', onClick: handleConnect, className: 'ws-builder__btn--connect' }}
      />

      {/* Config tabs — same row as the HTTP builder's Params / Headers / Body */}
      <div className="sb-tabs flex-shrink-0">
        {visibleTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`sb-tab${activeTab === tab ? ' sb-tab--active' : ''}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab content (capped height) */}
      <div className="flex-shrink-0 border-b border-gray-100" style={{ maxHeight: 180, overflowY: 'auto' }}>
        {activeTab === 'headers' && (
          <div className="editor-tab-body">
            <KvTable>
              {headers.map((h, idx) => (
                <KvRow
                  key={h._id ?? idx}
                  enabled={h.enabled}
                  onToggleEnabled={(v) => updateHeader(idx, 'enabled', v)}
                  checkboxTabIndex={-1}
                  showRemove={!(idx === headers.length - 1 && h.key === '')}
                  onRemove={() => removeHeader(idx)}
                  removeDisabled={isActive}
                  renderKey={
                    <input
                      type="text"
                      value={h.key}
                      onChange={(e) => updateHeader(idx, 'key', e.target.value)}
                      placeholder="Key"
                      aria-label="Header name"
                      className="request-builder-kv-input w-full rounded-md focus:outline-none"
                      disabled={isActive}
                    />
                  }
                  renderValue={
                    <input
                      type="text"
                      value={h.value}
                      onChange={(e) => updateHeader(idx, 'value', e.target.value)}
                      placeholder="Value"
                      aria-label="Header value"
                      className="request-builder-kv-input w-full rounded-md focus:outline-none"
                      disabled={isActive}
                    />
                  }
                />
              ))}
            </KvTable>
          </div>
        )}

        {activeTab === 'body' && method === 'POST' && (
          <div className="px-4 py-2">
            <textarea
              value={body}
              onChange={(e) => { setBody(e.target.value); onDataChange?.({ body: e.target.value }); }}
              placeholder={'{\n  "stream": true\n}'}
              className="sb-field sb-field--mono"
              disabled={isActive}
            />
          </div>
        )}

        {activeTab === 'auth' && (
          <div className="px-4 py-2 flex items-center gap-3">
            <CompactSelectPicker
              value={auth.type}
              onChange={(v) => {
                const next = { type: v };
                setAuth(next);
                onDataChange?.({ auth: next });
              }}
              options={[
                { value: 'none', label: 'No Auth' },
                { value: 'bearer', label: 'Bearer Token' },
              ]}
              disabled={isActive}
              aria-label="Auth type"
            />
            {auth.type === 'bearer' && (
              <input
                type="text"
                value={auth.token ?? ''}
                onChange={(e) => {
                  const next = { ...auth, token: e.target.value };
                  setAuth(next);
                  onDataChange?.({ auth: next });
                }}
                placeholder="Bearer token"
                aria-label="Bearer token"
                className="sb-field sb-field--mono flex-1 min-w-0"
                disabled={isActive}
              />
            )}
          </div>
        )}
      </div>

      {/* Event log — the shared table: newest first, the event's NAME as a tag, click to preview */}
      <div className="ws-builder__log flex flex-col flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <StreamLogTable
          key={request?.id}
          entries={events}
          dropped={dropped}
          onClear={() => setLog({ events: [], dropped: 0 })}
          filters={['all', 'received', 'events']}
          emptyText={isActive ? 'Waiting for events…' : 'Connect to start receiving events.'}
        />
      </div>
    </div>
  );
};

export default SSEBuilder;
